/**
 * The rating token a Smith answer carries, read out of its reply so the portal can offer the
 * rating natively (fr-docs `docs/smith-ams` F-030 / T-117), and out of the rating page's own
 * address.
 *
 * The reply renders in an iframe with `sandbox=""` — no scripts, no navigation — so the stars
 * at the bottom of an answer are inert there, by design. Rather than loosen the sandbox, the
 * run page reads the token out of the reply and renders the same form the public page uses.
 *
 * Only the TOKEN is taken, never the URL: the portal relays the token to fr-docs, which
 * verifies it (HMAC, expiry, execution + contractor). A crafted link in a reply can therefore
 * neither point the reader at another host nor rate an answer the token was not minted for —
 * at worst it shows the invalid-link state.
 *
 * A link has one of two forms:
 *  - `/avaliar?pt=2#<token>` — fr-docs' links since the token moved to the FRAGMENT, which a
 *    browser never sends, so no proxy's access log ever records it;
 *  - `/avaliar/<token>` — the links already sent before that.
 * `/rate` is an alias of `/avaliar` in both.
 */
import { isFeedbackTokenShape } from '@portal/contracts'

const RATING_LINK =
  /\/(?:avaliar|rate)(?:\/|\?[^#"'<>\s]*#|#)(v1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]{43})(?=$|[?#"'&<>\s/])/gi

/**
 * The rating token of THIS answer in a reply, or null. A reply can quote an earlier answer,
 * whose link would rate that other run; so with the run's id, only a token minted for it is
 * taken. Without one, the last link in the reply — the answer's own, below any quote.
 */
export function ratingTokenIn(message: string | undefined | null, executionId?: string): string | null {
  if (!message) return null
  const tokens = [...message.matchAll(RATING_LINK)].map((m) => m[1]).filter(isFeedbackTokenShape)
  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i] as string
    if (executionId === undefined || readRatingToken(token)?.executionId === executionId) return token
  }
  return null
}

export interface RatingTokenFacts {
  executionId?: string
  /** When the link stops working. */
  expiresAt?: Date
}

/**
 * What a token's payload says. UNVERIFIED — only fr-docs holds the key that checks it — so
 * this informs the page (which run, until when) and never authorizes anything.
 */
export function readRatingToken(token: string): RatingTokenFacts | null {
  const body = token.split('.')[1]
  if (!body) return null
  try {
    const base64 = body.replace(/-/g, '+').replace(/_/g, '/')
    const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    const payload = JSON.parse(new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)))) as {
      e?: unknown
      x?: unknown
    }
    return {
      ...(typeof payload.e === 'string' ? { executionId: payload.e } : {}),
      ...(typeof payload.x === 'number' && Number.isFinite(payload.x) ? { expiresAt: new Date(payload.x * 1000) } : {})
    }
  } catch {
    return null
  }
}

/** The rating page's token: the path's (`/avaliar/<token>`, a link already sent), else the fragment's. */
export function tokenFromLocation(pathToken: string | undefined, hash: string): string {
  const raw = pathToken ?? hash.replace(/^#/, '')
  try {
    return decodeURIComponent(raw).trim()
  } catch {
    return raw.trim()
  }
}
